/**
 * AddressViewModel - Platform-agnostic address and delivery management
 * Handles address search, validation, and delivery preferences management
 * 
 * Sheet Components: SheetContentDeliveryAddress, SheetContentAddressDetails
 */

import { useCallback, useState, useEffect, useMemo } from 'react'
import type { IPlatformServices } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'
import type { DeliveryAddress, DeliveryPreferenceValue } from '@restaurant/core'

import type {
  IAddressService,
  AddressSearchResult,
  DeliveryAddressData,
  AddressValidationResult,
  AddressDetailsFormData,
  AddressSummary
} from '../contracts'
import type { 
  SheetContentDeliveryAddressProps 
} from '@restaurant/core/components/SheetContentDeliveryAddress/SheetContentDeliveryAddress.types'
import type { 
  SheetContentAddressDetailsProps,
  AddressHeaderData
} from '@restaurant/core/components/SheetContentAddressDetails/SheetContentAddressDetails.types'
import type { 
  AddressDetailsData 
} from '@restaurant/core/components/FieldGroupAddressDetails/FieldGroupAddressDetails.types'

/** Map backend dropOffOption to form preference value */
function mapDropOffToForm(option?: 'handItToMe' | 'leaveItAtMyDoor'): DeliveryPreferenceValue {
  return option === 'handItToMe' ? 'hand' : 'door'
}

/** Map form preference value to backend dropOffOption */
export function mapFormToDropOff(pref?: string): 'handItToMe' | 'leaveItAtMyDoor' {
  return pref === 'hand' ? 'handItToMe' : 'leaveItAtMyDoor'
}

/**
 * AddressViewModel dependencies for service injection
 */
export interface AddressViewModelDependencies {
  addressService?: IAddressService
}

/**
 * AddressViewModel state interface
 */
export interface AddressViewModelState {
  /** Current search query */
  searchQuery: string
  /** Array of address search results */
  searchResults: AddressSearchResult[]
  /** Currently selected address */
  selectedAddress: AddressSearchResult | null
  /** Address details form data */
  addressDetails: AddressDetailsFormData
  /** Validation result for selected address */
  validationResult: AddressValidationResult | null
  /** Current sheet view: search or details */
  currentSheet: 'search' | 'details'
  /** General loading state */
  loading: boolean
  /** Search-specific loading state */
  searchLoading: boolean
  /** Validation-specific loading state */
  validationLoading: boolean
  /** Error message */
  error: string | null
  /** Whether address can be confirmed */
  canConfirmAddress: boolean
}

/**
 * AddressViewModel actions interface
 */
export interface AddressViewModelActions {
  /** Update search query */
  setSearchQuery: (query: string) => void
  /** Select an address from search results */
  selectAddress: (address: AddressSearchResult) => void
  /** Navigate to address details sheet */
  goToAddressDetails: () => void
  /** Navigate back to search sheet */
  goBackToSearch: () => void
  /** Update address details form field */
  updateAddressDetails: (field: keyof AddressDetailsFormData, value: unknown) => void
  /** Save complete address details */
  saveAddressDetails: (addressData: DeliveryAddressData) => Promise<void>
  /** Clear current error */
  clearError: () => void
  /** Navigate to edit address */
  editAddress: () => void
  /** Confirm and save selected address */
  confirmAddress: () => Promise<void>
  /** Get props for SheetContentDeliveryAddress */
  getChangeDeliveryAddressProps: () => Partial<SheetContentDeliveryAddressProps>
  /** Get props for SheetContentAddressDetails */
  getAddressDetailsProps: () => Partial<SheetContentAddressDetailsProps>
  /** Prefill selectedAddress and addressDetails from a saved DeliveryAddress */
  prefillFromAddress: (address: DeliveryAddress) => void
  /** Returns true if addressDetails has changed since the last prefill */
  hasDetailsChanged: () => boolean
  /** Reset VM to pristine state — clears all observable VM state to initial values */
  reset: () => void
}

/**
 * Combined AddressViewModel interface
 */
export interface AddressViewModel extends AddressViewModelState, AddressViewModelActions {}

/**
 * Platform-agnostic AddressViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createAddressViewModel = (
  services: IPlatformServices,
  dependencies?: AddressViewModelDependencies
) => {
  return (): AddressViewModel => {
    // Dependencies
    const addressService = dependencies?.addressService

    // State management
    const [searchQuery, setSearchQueryState] = useState('')
    const [searchResults, setSearchResults] = useState<AddressSearchResult[]>([])
    const [selectedAddress, setSelectedAddress] = useState<AddressSearchResult | null>(null)
    const [addressDetails, setAddressDetails] = useState<AddressDetailsFormData>({
      unitNumber: '',
      entryCode: '',
      buildingName: '',
      deliveryPreference: 'door',
      deliveryNotes: '',
    })
    const [validationResult, setValidationResult] = useState<AddressValidationResult | null>(null)
    const [currentSheet, setCurrentSheet] = useState<'search' | 'details'>('search')
    const [loading, setLoading] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false)
    const [validationLoading, setValidationLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [originalDetailsSnapshot, setOriginalDetailsSnapshot] = useState<AddressDetailsFormData | null>(null)

    // Debounced address search (300ms, minimum 3 characters)
    useEffect(() => {
      if (!searchQuery.trim() || searchQuery.trim().length < 3 || !addressService) {
        setSearchResults([])
        return
      }

      setSearchLoading(true)
      const timeoutId = setTimeout(async () => {
        try {
          const results = await addressService.searchAddresses(searchQuery.trim())
          setSearchResults(results)
          setError(null)
        } catch (err) {
          safeGetLogger()?.error('Address search failed', { domain: 'delivery', error: String(err) })
          setError(err instanceof Error ? err.message : 'Search failed')
          setSearchResults([])
        } finally {
          setSearchLoading(false)
        }
      }, 300)

      return () => {
        clearTimeout(timeoutId)
        setSearchLoading(false)
      }
    }, [searchQuery, addressService])

    // Load initial address details
    useEffect(() => {
      const loadAddressDetails = async () => {
        if (!addressService) return

        try {
          const details = await addressService.getAddressDetails()
          setAddressDetails(details)
        } catch (err) {
          safeGetLogger()?.error('Failed to load address details', { domain: 'delivery', error: String(err) })
        }
      }

      loadAddressDetails()
    }, [addressService])

    // Computed properties
    const canConfirmAddress = useMemo(() => {
      return selectedAddress !== null && 
             (validationResult?.isDeliverable !== false) && 
             !validationLoading && 
             !loading
    }, [selectedAddress, validationResult, validationLoading, loading])

    // Actions
    const setSearchQuery = useCallback((query: string) => {
      setSearchQueryState(query)
      setError(null)
      
      if (!query.trim()) {
        setSearchResults([])
      }
    }, [])

    const selectAddress = useCallback((address: AddressSearchResult) => {
      setSelectedAddress(address)
      setError(null)
    }, [])

    const updateAddressDetails = useCallback((field: keyof AddressDetailsFormData, value: unknown) => {
      setAddressDetails(prev => ({
        ...prev,
        [field]: value,
      }))
    }, [])

    const saveAddressDetails = useCallback(async (addressData: DeliveryAddressData) => {
      if (!addressService) {
        setError('Address service not available')
        return
      }

      setLoading(true)
      setError(null)

      try {
        await addressService.saveAddressDetails(addressData)
      } catch (err) {
        safeGetLogger()?.error('Failed to save address details', { domain: 'delivery', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to save address')
      } finally {
        setLoading(false)
      }
    }, [addressService])

    const clearError = useCallback(() => {
      setError(null)
    }, [])

    const goToAddressDetails = useCallback(() => {
      setCurrentSheet('details')
    }, [])

    const goBackToSearch = useCallback(() => {
      setCurrentSheet('search')
      setSearchQueryState('')
      setSearchResults([])
      setError(null)
    }, [])

    const editAddress = useCallback(() => {
      goBackToSearch()
    }, [goBackToSearch])

    const confirmAddress = useCallback(async () => {
      if (!selectedAddress || !canConfirmAddress || !addressService) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Combine selected address with form details
        const completeAddressData: DeliveryAddressData = {
          street: selectedAddress.street,
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
          country: selectedAddress.country || 'US',
          ...addressDetails,
          // Map the form vocabulary ('door'|'hand') to the backend save enum.
          // (Previously the form value was passed through untyped, so the
          // adapter's `=== 'hand_to_me'` check never matched and "hand it to me"
          // was silently never persisted.)
          deliveryPreference:
            addressDetails.deliveryPreference === 'hand' ? 'hand_to_me' : 'leave_at_door',
        }

        await addressService.saveAddressDetails(completeAddressData)
        
        // Update delivery address summary
        const addressSummary: AddressSummary = {
          addressLine1: selectedAddress.addressLine1,
          addressLine2: selectedAddress.addressLine2,
          deliveryInstructions: addressDetails.deliveryNotes || undefined,
        }
        await addressService.updateDeliveryAddress(addressSummary)

        // Navigate back
        services.navigationService.goBack()
      } catch (err) {
        safeGetLogger()?.error('Failed to confirm address', { domain: 'delivery', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to confirm address')
      } finally {
        setLoading(false)
      }
    }, [selectedAddress, canConfirmAddress, addressDetails, addressService, services.navigationService])

    const prefillFromAddress = useCallback((address: DeliveryAddress) => {
      setSelectedAddress({
        id: address.id,
        addressLine1: address.street,
        addressLine2: `${address.city}, ${address.state} ${address.zipCode}`,
        street: address.street,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        latitude: address.latitude,
        longitude: address.longitude,
        country: 'US',
      })
      const details: AddressDetailsFormData = {
        unitNumber: address.apartment || '',
        entryCode: address.entryCode || '',
        buildingName: address.buildingName || '',
        deliveryPreference: mapDropOffToForm(address.deliveryDetails?.dropOffOption),
        deliveryNotes: address.deliveryDetails?.instructions || '',
      }
      setAddressDetails(details)
      setOriginalDetailsSnapshot({ ...details })
    }, [])

    const hasDetailsChanged = useCallback((): boolean => {
      if (!originalDetailsSnapshot) return false
      return (
        addressDetails.unitNumber !== originalDetailsSnapshot.unitNumber ||
        addressDetails.entryCode !== originalDetailsSnapshot.entryCode ||
        addressDetails.buildingName !== originalDetailsSnapshot.buildingName ||
        addressDetails.deliveryPreference !== originalDetailsSnapshot.deliveryPreference ||
        addressDetails.deliveryNotes !== originalDetailsSnapshot.deliveryNotes
      )
    }, [addressDetails, originalDetailsSnapshot])

    const reset = useCallback(() => {
      setSelectedAddress(null)
      setAddressDetails({
        unitNumber: '',
        entryCode: '',
        buildingName: '',
        deliveryPreference: 'door',
        deliveryNotes: '',
      })
      setOriginalDetailsSnapshot(null)
      setCurrentSheet('search')
      setSearchQueryState('')
      setSearchResults([])
      setError(null)
      setLoading(false)
      setSearchLoading(false)
      setValidationLoading(false)
      setValidationResult(null)
    }, [])

    // Component integration props
    const getChangeDeliveryAddressProps = useCallback((): Partial<SheetContentDeliveryAddressProps> => {
      return {
        searchValue: searchQuery,
        searchPlaceholder: 'Enter delivery address',
        addresses: searchResults.map(result => ({
          id: result.id,
          addressLine1: result.addressLine1,
          addressLine2: result.addressLine2,
        })),
        disabled: loading || validationLoading,
        onSearchChange: setSearchQuery,
        onSearchSubmit: () => {
          // Trigger search if not already searching
          if (searchQuery.trim() && !searchLoading) {
            // Search is already handled by useEffect
          }
        },
        onAddressSelect: (addressResult) => {
          const fullAddress = searchResults.find(result => result.id === addressResult.id)
          if (fullAddress) {
            selectAddress(fullAddress)
          }
        },
      }
    }, [searchQuery, searchResults, loading, validationLoading, searchLoading, setSearchQuery, selectAddress])

    const getAddressDetailsProps = useCallback((): Partial<SheetContentAddressDetailsProps> => {
      const addressHeader: AddressHeaderData = selectedAddress ? {
        addressLine1: selectedAddress.addressLine1,
        addressLine2: selectedAddress.addressLine2,
      } : {
        addressLine1: '',
        addressLine2: '',
      }

      const formAddressDetails: AddressDetailsData = {
        unitNumber: addressDetails.unitNumber || '',
        entryCode: addressDetails.entryCode || '',
        buildingName: addressDetails.buildingName || '',
        deliveryPreference: addressDetails.deliveryPreference || 'door',
        deliveryNotes: addressDetails.deliveryNotes || '',
      }

      return {
        addressHeader,
        addressDetails: formAddressDetails,
        onAddressDetailsChange: (fieldName, fieldValue, _allValues) => {
          updateAddressDetails(fieldName, fieldValue)
        },
        onEditAddress: editAddress,
        onConfirmAddress: confirmAddress,
        disabled: loading || validationLoading,
        loading: loading,
      }
    }, [selectedAddress, addressDetails, loading, validationLoading, updateAddressDetails, editAddress, confirmAddress])

    return {
      // State
      searchQuery,
      searchResults,
      selectedAddress,
      addressDetails,
      validationResult,
      currentSheet,
      loading,
      searchLoading,
      validationLoading,
      error,
      canConfirmAddress,

      // Actions
      setSearchQuery,
      selectAddress,
      goToAddressDetails,
      goBackToSearch,
      updateAddressDetails,
      saveAddressDetails,
      clearError,
      editAddress,
      confirmAddress,
      getChangeDeliveryAddressProps,
      getAddressDetailsProps,
      prefillFromAddress,
      hasDetailsChanged,
      reset,
    }
  }
}

// Re-export types for convenient importing
export type {
  AddressSearchResult,
  DeliveryAddressData,
  AddressValidationResult,
  AddressDetailsFormData,
  AddressSummary,
  IAddressService,
}
